/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
class SummarizerHostAPI {
	//The role of the summarizer host API is to communicate with the host application (usu. Explore)
	//The host registers a set of callbacks using the activate api and is notified of changes to stats via those callbacks.
	constructor(feature, widgetApi, dashboardApi) {
		this.feature = feature;
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.statsReadyBeforeActivated = false;
	}

	/**
	 * Activate the summarizer by supplying callbacks from the host application
	 * @param options - The options which includes:
	 *					options.cb - a callback for updating default summary data
	 *					options.cbSel - a callback for updating selection-based summary data
	 *					options.cbDomain - a callback for handling data domains.
	 * @returns a promise which is resolved immediately (or, in case where summaries must be
 	 * 			updated on activate, when the update is complete.  (mostly for testing purposes).
	 */
	activate(options) {
		this.summaryCollector = this.feature.getSummaryCollector();
		this.selectionSummaryCollector = this.feature.getSelectionSummaryCollector();

		this.cb = options.cb;
		//cbSel is defined for selection callbacks.
		this.cbSel = options.cbSel;
		//cbDomain is defined for domain updating callbacks.
		this.cbDomain = options.cbDomain;

		this.feature.setActivated(true);

		if (this.statsReadyBeforeActivated) {
			//If we attempted to call updateData or updateSelectionData before
			//callbacks were set up, the 'statsReadyBeforeActivated' flag is set (and we need to call again).
			this.statsReadyBeforeActivated=false;
			return this._onStatsReady().then(() => {
				return this._onSelectedStatsReady();
			});
		}
		return Promise.resolve();
	}

	//Called during render to update the default stats for this render (cached)
	updateSummaries() {
		return this._onStatsReady();
	}

	//TODO: This header should probably be taken care of by the host application.
	_addHeader() {
		return [
			{ id: 'min', name: 'Min' },
			{ id: 'max', name: 'Max' },
			{ id: 'avg', name: 'Average' },
			{ id: 'total', name: 'Total' },
			{ id: 'totalPercent', name: '%Total' }
		];
	}

	//Called on a selection change to update the stats for the selections
	updateSelectionSummaries() {
		this._onSelectedStatsReady();
	}

	//Notify the host application of an overall stats update (ie: render/reRender)
	_onStatsReady() {
		let stats = this.summaryCollector && this.summaryCollector.getSummaryTableJSON() || {};
		stats.header = this._addHeader();
		//FOR TESTING: console.log('RENDER:', JSON.stringify(stats));

		if (this.cb) {
			this.cb( stats );
		} else {
			this.statsReadyBeforeActivated=true;
		}
		return this.cbDomain && this.cbDomain( stats ) || Promise.resolve();
	}

	//Notify the host application of a selection stats update.
	_onSelectedStatsReady() {
		let stats = this.selectionSummaryCollector && this.selectionSummaryCollector.getSummaryTableJSON() || {};
		stats.header = this._addHeader();
		//FOR TESTING: console.log('SELECT:', JSON.stringify(stats));

		if (this.cbSel) {
			this.cbSel( stats );
		} else {
			this.statsReadyBeforeActivated=true;
		}
	}
}

export default SummarizerHostAPI;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/SummarizerHostAPI.js